import * as CognitoSDK from '@aws-sdk/client-cognito-identity-provider';
import { Auth } from 'aws-amplify';
import { AWS_API, AWS_CONFIGURATION } from 'config';
import { InitialLoginContextProps } from 'types/auth';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { clearCognitoSession, getIdToken, setCognitoSession } from './cognitoTokenStore';

export const initialState: InitialLoginContextProps = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

export const awsAdminClient = new CognitoSDK.CognitoIdentityProviderClient({
    region: AWS_CONFIGURATION.Auth.region,
    credentials: {
        accessKeyId: AWS_API.accessKeyId,
        secretAccessKey: AWS_API.secretAccessKey
    }
});

export const cleanLocalStorage = () => {
    clearCognitoSession();
    localStorage.removeItem('shouldSkipSetupMFA');
    localStorage.removeItem('associateSoftwareToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('tenantSelected');
    localStorage.removeItem('tenantPath');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('role_id');
};

/**
 * Get temporary AWS credentials using AWS Cognito Identity Pool.
 * Exchanges the Cognito user pool ID token for temporary AWS credentials (in-memory cache or Amplify session).
 * @returns {Promise<{
 *   accessKeyId: string;
 *   secretAccessKey: string;
 *   sessionToken: string;
 *   expiration: Date;
 * }>}
 * @throws Error if there is an error fetching the temporary AWS credentials
 */
const resolveCognitoIdTokenForIdentityPool = async (): Promise<string> => {
    const cached = getIdToken()?.trim();
    if (cached) return cached;

    try {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();
        const accessToken = session.getAccessToken().getJwtToken();
        setCognitoSession(accessToken, idToken);
        return idToken;
    } catch {
        throw new Error('Unable to upload: your Cognito session is missing or expired. Please sign out and sign in again, then retry.');
    }
};

export const getTemporaryAWSCredentials = async () => {
    try {
        const idToken = await resolveCognitoIdTokenForIdentityPool();

        // Use AWS Cognito Identity Pool to exchange JWT token for temporary AWS credentials
        const credentialsProvider = fromCognitoIdentityPool({
            clientConfig: {
                region: AWS_API.region
            },
            identityPoolId: AWS_API.identityPoolId, // Replace with your Identity Pool ID or pass as parameter
            logins: {
                [`cognito-idp.${AWS_API.region}.amazonaws.com/${AWS_API.poolId}`]: idToken
            }
        });

        // Retrieve the credentials
        const credentials = await credentialsProvider();

        // Return credentials
        return {
            accessKeyId: credentials.accessKeyId,
            secretAccessKey: credentials.secretAccessKey,
            sessionToken: credentials.sessionToken,
            expiration: credentials.expiration
        };
    } catch (error) {
        console.error('Error fetching temporary AWS credentials:', error);
        throw error;
    }
};

export const setSession = (newServiceToken?: string | null, newIdToken?: string | null) => {
    setCognitoSession(newServiceToken, newIdToken);
};

export const AUTH_ROUTES = [
    '/login',
    '/register',
    '/forgot',
    '/recover',
    '/reset',
    '/user-confirmation',
    '/change',
    '/configured',
    '/setupmfa',
    '/select-tenant'
] as const;

let forceLogoutInFlight = false;

// Hard-redirect the user to /login after auth state becomes unrecoverable
// (e.g. backend refresh-token rotation returns UNAUTHENTICATED). Safe to
// call from non-React contexts such as the Apollo error link. Idempotent
// within a single page lifetime — repeated calls are no-ops.
//
// Wipes the entire localStorage (preserving only redirectPath) and replaces
// the URL synchronously, so React Router / AuthGuards never have a chance
// to bounce the user through /select-tenant on the way out.
export const forceLogoutToLogin = () => {
    if (forceLogoutInFlight) return;
    forceLogoutInFlight = true;

    const { pathname, search } = window.location;
    const shouldPreservePath = pathname && !AUTH_ROUTES.some((route) => pathname.includes(route));
    const redirectPath = shouldPreservePath ? `${pathname}${search}` : null;

    try {
        window.localStorage.clear();
        if (redirectPath) {
            window.localStorage.setItem('redirectPath', redirectPath);
        }
    } catch {
        // best-effort — we still need to navigate away
    }

    // Full page replace tears down Apollo cache, Redux store, and any
    // in-flight queries that would otherwise re-trigger the failed refresh.
    window.location.replace('/login');
};
